import { RemixApp } from '@remix-ui/app'
import axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import * as packageJson from '../../../../../package.json'
import { fileSystem, fileSystems } from '../files/fileSystem'
import { indexedDBFileSystem } from '../files/filesystems/indexedDB'
import { localStorageFS } from '../files/filesystems/localStorage'
import { fileSystemUtility, migrationTestData } from '../files/filesystems/fileSystemUtility'
import './styles/preload.css'
import isElectron from 'is-electron'
const _paq = (window._paq = window._paq || [])

_paq.push(['trackEvent', 'App', 'Preload', 'start'])

export const Preload = (props: any) => {
  const [tip, setTip] = useState<string>('')
  const [supported, setSupported] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [showDownloader, setShowDownloader] = useState<boolean>(false)
  const remixFileSystems = useRef<fileSystems>(new fileSystems())
  const remixIndexedDB = useRef<fileSystem>(new indexedDBFileSystem())
  const localStorageFileSystem = useRef<fileSystem>(new localStorageFS())
  // url parameters to e2e test the fallbacks and error warnings
  const testmigrationFallback = useRef<boolean>(
    window.location.hash.includes('e2e_testmigration_fallback=true') && window.location.host === '127.0.0.1:8080' && window.location.protocol === 'http:'
  )
  const testmigrationResult = useRef<boolean>(
    window.location.hash.includes('e2e_testmigration=true') && window.location.host === '127.0.0.1:8080' && window.location.protocol === 'http:'
  )
  const testBlockStorage = useRef<boolean>(
    window.location.hash.includes('e2e_testblock_storage=true') && window.location.host === '127.0.0.1:8080' && window.location.protocol === 'http:'
  )

  function loadAppComponent() {
    import('../../app')
      .then((AppComponent) => {
        const appComponent = new AppComponent.default()
        appComponent.run().then(() => {
          props.root.render(<RemixApp app={appComponent} />)
        })
      })
      .catch((err) => {
        _paq.push(['trackEvent', 'App', 'PreloadError', err && err.message])
        console.error('Error loading Remix:', err)
        setError(true)
      })
  }

  const downloadBackup = async () => {
    setShowDownloader(false)
    const fsUtility = new fileSystemUtility()
    await fsUtility.downloadBackup(remixFileSystems.current.fileSystems['localstorage'])
    await migrateAndLoad()
  }

  const migrateAndLoad = async () => {
    setShowDownloader(false)
    const fsUtility = new fileSystemUtility()
    const migrationResult = await fsUtility.migrate(localStorageFileSystem.current, remixIndexedDB.current)
    _paq.push(['trackEvent', 'Migrate', 'result', migrationResult ? 'success' : 'fail'])
    await setFileSystems()
  }

  const setFileSystems = async () => {
    const fsLoaded = await remixFileSystems.current.setFileSystem([
      testmigrationFallback.current || testBlockStorage.current ? null : remixIndexedDB.current,
      testBlockStorage.current ? null : localStorageFileSystem.current
    ])
    if (fsLoaded) {
      console.log(fsLoaded.name + ' activated')
      _paq.push(['trackEvent', 'Storage', 'activate', fsLoaded.name])
      loadAppComponent()
    } else {
      _paq.push(['trackEvent', 'Storage', 'error', 'no supported storage'])
      setSupported(false)
    }
  }

  const testmigration = async () => {
    if (testmigrationResult.current) {
      const fsUtility = new fileSystemUtility()
      fsUtility.populateWorkspace(migrationTestData, remixFileSystems.current.fileSystems['localstorage'].fs)
    }
  }

  useEffect(() => {
    if (isElectron()) {
      loadAppComponent()
      return
    }
    async function loadStorage() {
      ; (await remixFileSystems.current.addFileSystem(remixIndexedDB.current)) || _paq.push(['trackEvent', 'Storage', 'error', 'indexedDB not supported'])
        ; (await remixFileSystems.current.addFileSystem(localStorageFileSystem.current)) || _paq.push(['trackEvent', 'Storage', 'error', 'localstorage not supported'])
      await testmigration()
      remixIndexedDB.current.loaded && (await remixIndexedDB.current.checkWorkspaces())
      localStorageFileSystem.current.loaded && (await localStorageFileSystem.current.checkWorkspaces())
      remixIndexedDB.current.loaded && (remixIndexedDB.current.hasWorkSpaces || !localStorageFileSystem.current.hasWorkSpaces ? await setFileSystems() : setShowDownloader(true))
      !remixIndexedDB.current.loaded && (await setFileSystems())
    }
    loadStorage()

    const abortController = new AbortController()
    const signal = abortController.signal
    async function showRemixTips() {
      const response = await axios.get('https://raw.githubusercontent.com/remix-project-org/remix-dynamics/main/ide/tips.json', { signal })
      if (signal.aborted) return
      const tips = response.data
      const index = Math.floor(Math.random() * (tips.length - 1))
      setTip(tips[index])
    }
    try {
      showRemixTips()
    } catch (e) {
      console.log(e)
    }
    return () => {
      abortController.abort();
    };
  }, [])

  return (
    <>
      <div className="preload-container">
        <div className="preload-logo pb-4">
          {logo}
          <div className="info-secondary splash">
            LIGHTCHAIN
            <br />
            <span className="version"> v{packageJson.version}</span>
          </div>
        </div>
        {!supported ? (
          <div className="preload-info-container alert alert-warning">
            Your browser does not support any of the filesystems required by Remix. Either change the settings in your browser or use a supported browser.
          </div>
        ) : null}
        {error ? (
          <div className="preload-info-container alert alert-danger text-left">
            An unknown error has occurred while loading the application.
            <br></br>
            Doing a hard refresh might fix this issue:<br></br>
            <div className="pt-2">
              Windows:<br></br>- Chrome: CTRL + F5 or CTRL + Reload Button
              <br></br>- Firefox: CTRL + SHIFT + R or CTRL + F5<br></br>
            </div>
            <div className="pt-2">
              MacOS:<br></br>- Chrome & FireFox: CMD + SHIFT + R or SHIFT + Reload Button<br></br>
            </div>
            <div className="pt-2">
              Linux:<br></br>- Chrome & FireFox: CTRL + SHIFT + R<br></br>
            </div>
          </div>
        ) : null}
        {showDownloader ? (
          <div className="preload-info-container alert alert-info">
            This app will be updated now. Please download a backup of your files now to make sure you don't lose your work.
            <br></br>
            You don't need to do anything else, your files will be available when the app loads.
            <div
              onClick={async () => {
                await downloadBackup()
              }}
              data-id="downloadbackup-btn"
              className="btn btn-primary mt-1"
            >
              download backup
            </div>
            <div
              onClick={async () => {
                await migrateAndLoad()
              }}
              data-id="skipbackup-btn"
              className="btn btn-primary mt-1"
            >
              skip backup
            </div>
          </div>
        ) : null}
        {supported && !error && !showDownloader ? (
          <div>
            <div className='text-center'>
              <i className="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            {tip && <div className='remix_tips text-center mt-3'>
              <div><b>DID YOU KNOW</b></div>
              <span>{tip}</span>
            </div>}
          </div>
        ) : null}
      </div>
    </>
  )
}

const logo = (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={100}
      height={107}
      viewBox="0 0 100 107"
      fill="none"
    >
      <path
        d="M95.5558 57.1492C95.5622 57.1366 95.5688 57.1245 95.575 57.1119C101.125 46.0379 101.465 35.2552 96.5334 26.7499C92.14 19.1728 84.0796 14.3032 73.601 12.7989C67.053 4.51543 58.7874 0 50 0C42.3838 0 35.1595 3.39236 29.1055 9.68895C20.3781 10.3812 13.1733 13.811 8.35938 19.6874C2.1257 27.2982 0.73054 37.9964 4.43121 49.8113C4.43528 49.8246 4.43993 49.8377 4.4442 49.851C4.438 49.8634 4.43121 49.8757 4.42501 49.8881C-1.12503 60.9621 -1.46529 71.7448 3.46661 80.2501C7.86002 87.8274 15.9204 92.6968 26.399 94.2013C32.947 102.485 41.2126 107 50 107C57.618 107 64.844 103.606 70.8988 97.3066C79.6224 96.6136 86.8281 93.1877 91.6406 87.3126C97.8743 79.7018 99.2695 69.0036 95.5688 57.1887C95.5647 57.1756 95.5601 57.1623 95.5558 57.1492ZM92.7533 28.9231C96.3799 35.1782 96.5638 43.0339 93.3549 51.3644C92.0851 48.5184 90.5526 45.7105 88.78 42.9724L87.1887 48.7615C88.6718 51.4154 89.8941 54.0888 90.8432 56.7392C89.3483 59.4632 87.5509 62.1166 85.4856 64.6653C86.0034 61.0331 86.2774 57.2981 86.2774 53.5002C86.2774 43.4227 84.4015 33.0876 80.2308 23.8326C79.3187 21.8088 78.2935 19.8349 77.1472 17.9326C84.2228 19.7763 89.6372 23.5497 92.7533 28.9231ZM18.0879 53.5002C18.0879 47.4544 18.802 41.6614 20.1041 36.3071C24.1616 32.4575 28.8574 28.9644 34.044 25.9828C40.1468 22.4747 46.5255 19.91 52.8405 18.364C58.7256 20.8587 64.624 24.3741 70.169 28.8768C74.0741 32.0477 77.5332 35.4883 80.509 39.0753C81.4203 43.6388 81.9121 48.4826 81.9121 53.5002C81.9121 59.5458 81.198 65.3388 79.8961 70.6929C75.8386 74.5425 71.1428 78.0356 65.956 81.0172C59.8555 84.524 53.479 87.0944 47.1661 88.6406C41.2791 86.146 35.3783 82.6276 29.8313 78.1232H29.831C25.9259 74.9523 22.4668 71.5117 19.491 67.9247C18.5797 63.3614 18.0879 58.5178 18.0879 53.5002ZM67.3929 12.309C64.52 12.2596 61.581 12.4431 58.6089 12.8461L62.2284 16.8227C63.6472 16.7083 65.0544 16.6463 66.4443 16.6463C67.4318 16.6463 68.4115 16.6753 69.3808 16.7339C70.0194 16.7727 70.6482 16.8252 71.2687 16.8889C73.9786 20.6275 76.2733 25.0662 78.0296 30.0205C76.4103 28.4618 74.7086 26.9542 72.928 25.5082C70.8948 23.8572 68.782 22.3032 66.5967 20.8576C64.4296 19.424 62.1819 18.1408 59.9094 16.8823C58.2673 15.9729 56.5309 15.183 54.7957 14.4672C54.1768 14.2117 53.6571 13.9954 53.2079 13.8119C47.8805 11.6904 42.481 10.3233 37.2063 9.79719C36.657 9.74258 36.1133 9.70053 35.5724 9.66445C39.9077 6.26534 44.811 4.34642 50 4.34642C56.4116 4.34642 62.3862 7.27631 67.3929 12.309ZM11.7423 22.4344C15.0215 18.4312 19.6137 15.863 24.9981 14.6659C24.6849 15.1052 24.3749 15.5503 24.0709 16.0077C21.4914 19.8913 19.3655 24.267 17.7211 28.9858L23.1096 27.0576C25.2327 21.953 27.9525 17.4918 31.1145 13.9042C31.5776 13.8874 32.0429 13.8754 32.5132 13.8754C36.737 13.8754 41.2438 14.5517 45.85 15.8769C41.1163 17.4794 36.4117 19.6032 31.8613 22.219C28.7847 23.9875 25.8558 25.9727 23.0196 28.1002C20.9678 29.6392 18.9128 31.2525 17.1125 33.0832C16.7782 33.4109 16.4752 33.7079 16.2004 33.9768C16.1946 33.999 16.1892 34.0214 16.1831 34.0436C12.845 37.3267 9.93281 40.8466 7.52422 44.5236C5.75875 36.0827 7.00161 28.2222 11.7423 22.4344ZM7.24673 78.0769C3.62008 71.822 3.43619 63.9663 6.64507 55.6356C8.06988 58.829 9.82566 61.9746 11.88 65.0272L13.2694 59.042C11.5789 56.1251 10.202 53.1792 9.15695 50.2608C10.6517 47.5368 12.4491 44.8834 14.5144 42.3347C13.9966 45.9671 13.7226 49.7023 13.7226 53.5002C13.7226 56.1303 13.8457 58.7873 14.0903 61.4062C14.985 70.9867 17.8446 80.757 22.8528 89.0674C15.7774 87.2237 10.3628 83.4505 7.24673 78.0769ZM32.6017 94.6858C32.9152 94.6912 33.2276 94.6997 33.5425 94.6997C36.2627 94.6997 39.0387 94.4915 41.8426 94.0906L38.1636 90.1492C35.6017 90.3741 33.0788 90.4154 30.6192 90.2661C29.9806 90.2275 29.3518 90.175 28.7313 90.1113C26.0212 86.3725 23.7265 81.9338 21.9702 76.9791C23.5897 78.538 25.2916 80.0458 27.0724 81.4918H27.072C31.1401 84.795 35.4391 87.6381 39.8506 89.9724C39.8434 89.9734 39.8362 89.9742 39.8291 89.9749C41.6449 90.9184 43.8971 92.0169 46.0604 92.878C46.8192 93.1801 47.4275 93.425 47.9259 93.6277C52.8843 95.499 57.8928 96.7141 62.7937 97.2028C63.3432 97.2576 63.8867 97.2995 64.4277 97.3356C60.0923 100.735 55.189 102.654 50 102.654C43.5863 102.654 37.6096 99.7216 32.6017 94.6858ZM88.2577 84.5656C84.9781 88.5692 80.3857 91.1397 75.0002 92.3368C75.3139 91.8967 75.6247 91.4507 75.9291 90.9923C78.4238 87.2368 80.4989 83.0241 82.1202 78.483L76.7286 80.3224C74.6295 85.2687 71.968 89.5989 68.8847 93.097C64.2592 93.2639 59.26 92.5954 54.1367 91.12C58.8748 89.5169 63.5844 87.3993 68.1387 84.781C71.4412 82.8827 74.5576 80.7836 77.4574 78.5216C77.4532 78.5322 77.4493 78.543 77.445 78.5536C78.9952 77.3192 80.8054 75.8001 82.3876 74.267C83.6826 73.0122 84.4458 72.2898 84.8917 71.8756C87.783 68.9046 90.3296 65.7532 92.4758 62.4766C94.2412 70.9173 92.9984 78.778 88.2577 84.5656ZM51.8085 33.8687L52.5029 48.5253C52.5437 49.3877 53.252 50.0692 54.119 50.0806L64.5883 50.2178C65.8808 50.2347 66.6462 51.6646 65.9396 52.7423L52.0368 73.948C51.1457 75.3072 49.0245 74.6789 49.0245 73.0557V58.5678C49.0245 57.6662 48.2905 56.9354 47.385 56.9354H36.2282C34.9339 56.9354 34.1487 55.5143 34.8443 54.4276C37.9607 49.5598 44.8766 38.8028 48.8153 33.0272C49.7005 31.7292 51.7343 32.3015 51.8085 33.8687Z"
        fill="url(#paint0_linear_51_98)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_51_98"
          x1="-1.92063e-08"
          y1="53.5"
          x2="101.966"
          y2="53.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#3005FA" />
          <stop offset={1} stopColor="#FF12FB" />
        </linearGradient>
      </defs>
    </svg>
  </>
)
